import { fw, c } from "../theme/designLintTokens";
/**
 * Unified design tokens — one system: BASE · SURFACE · AMBIENT · INTERACTIVE
 * Primary #3B82F6 aligns with portal shell + ambient glows.
 */

// ═══════════════════════════════════════════════════════════════════════════════
// BASE — page / canvas background (deepest layer)
// ═══════════════════════════════════════════════════════════════════════════════

export const BASE_DARK = '#020617';

// ═══════════════════════════════════════════════════════════════════════════════
// SURFACE — cards, toolbars, panels (elevated above BASE)
// ═══════════════════════════════════════════════════════════════════════════════

export const SURFACE_DARK = '#0B1120';

/** @deprecated Legacy name — use SURFACE_ELEVATED_TINT for subtle lift on dark UI */
export const SURFACE_DARK_ALT = 'rgba(255,255,255,0.08)';
export const SURFACE_ELEVATED_TINT = 'rgba(255,255,255,0.08)';
export const SURFACE_ELEVATED_DARK = SURFACE_ELEVATED_TINT;
export const SURFACE_SUBTLE_TINT = 'rgba(255,255,255,0.05)';
/** Alias — grid headers / subtle stripes on dark UI */
export const SURFACE_SUBTLE_DARK = SURFACE_SUBTLE_TINT;
export const SURFACE_LIGHT = '#F5F7FA';
export const SURFACE_LIGHT_ALT = '#E4E7EB';
export const HEADER_BG_LIGHT = '#FFFFFF';

// Portal shell (aliases — same hexes as BASE / SURFACE)
export const PORTAL_SHELL_FROM = SURFACE_DARK;
export const PORTAL_SHELL_TO = BASE_DARK;

/** Dashboard sidebar (dark) — login-aligned */
export const SIDEBAR_GLASS_DARK = 'rgba(2, 6, 23, 0.7)';
export const SIDEBAR_GLASS_LIGHT = 'rgba(255, 255, 255, 0.72)';
export const LOGIN_SHELL_GRADIENT = `linear-gradient(135deg, ${PORTAL_SHELL_FROM} 0%, ${PORTAL_SHELL_TO} 100%)`;

/** Dark page / banner gradient — same as portal shell (no legacy gray ramp) */
export const GRADIENT_SURFACE_DARK = LOGIN_SHELL_GRADIENT;
export const GRADIENT_SURFACE_LIGHT = 'linear-gradient(135deg, #F5F7FA 0%, #E4E7EB 100%)';

// ═══════════════════════════════════════════════════════════════════════════════
// AMBIENT — layered glow + noise (stack over BASE via AmbientBackground)
// ═══════════════════════════════════════════════════════════════════════════════

export const AMBIENT_BASE_DARK = LOGIN_SHELL_GRADIENT;
export const AMBIENT_GLOW_DARK = ['radial-gradient(circle at 15% 25%, rgba(59, 130, 246, 0.12), transparent 45%)', 'radial-gradient(circle at 85% 75%, rgba(99, 102, 241, 0.08), transparent 50%)', 'radial-gradient(circle at 52% 48%, rgba(59, 130, 246, 0.07), transparent 58%)', 'radial-gradient(circle at 68% 42%, rgba(255, 255, 255, 0.035), transparent 52%)'].join(', ');
export const AMBIENT_GLOW_LIGHT = ['radial-gradient(circle at 15% 25%, rgba(59, 130, 246, 0.07), transparent 45%)', 'radial-gradient(circle at 85% 72%, rgba(99, 102, 241, 0.08), transparent 50%)', 'radial-gradient(circle at 50% 50%, rgba(59, 130, 246, 0.06), transparent 58%)', 'radial-gradient(circle at 70% 45%, rgba(255, 255, 255, 0.35), transparent 55%)'].join(', ');
export const AMBIENT_NOISE_TEXTURE = c.c_repeating_radial_gradient_circle_at_0_0_rgba_255_255_255_0_02_transparent_1px;

/** Light shell (login right column) */
export const SHELL_LIGHT_FROM = '#F8FAFC';
export const SHELL_LIGHT_TO = '#F1F5F9';
export const BASE_LIGHT_GRADIENT = `linear-gradient(135deg, ${SHELL_LIGHT_FROM} 0%, ${SHELL_LIGHT_TO} 100%)`;
export const SLATE_900 = '#0f172a';
export const SLATE_900_RGB = '15, 23, 42';

// ═══════════════════════════════════════════════════════════════════════════════
// INTERACTIVE — primary brand, buttons, focus, row hovers
// ═══════════════════════════════════════════════════════════════════════════════

export const PRIMARY = '#3B82F6';
export const PRIMARY_DARK = '#2563EB';
export const PRIMARY_LIGHT = '#EFF6FF';
export const SECONDARY = '#52606D';
export const SECONDARY_DARK = '#3E4C59';
export const INDIGO = '#4f46e5';
export const INDIGO_LIGHT = '#ede9fe';
export const VIOLET = '#7c3aed';
export const VIOLET_DARK = '#6d28d9';
export const GRADIENT_PRIMARY = `linear-gradient(135deg, ${PRIMARY}, ${SECONDARY})`;
export const GRADIENT_PRIMARY_HOVER = `linear-gradient(135deg, ${PRIMARY_DARK}, ${SECONDARY_DARK})`;
export const GRADIENT_TEXT = `linear-gradient(90deg, ${PRIMARY}, ${SECONDARY})`;
export const SHADOW_PRIMARY = '0 4px 14px rgba(59,130,246,0.35)';
export const SHADOW_PRIMARY_HOVER = '0 6px 20px rgba(59,130,246,0.45)';

/** Outline + login secondary — slate (light) / white alpha (dark) */
export const LOGIN_BTN_BORDER = `rgba(${SLATE_900_RGB}, 0.12)`;
export const LOGIN_BTN_BORDER_HOVER = `rgba(${SLATE_900_RGB}, 0.18)`;
export const LOGIN_BTN_SHADOW_HOVER = '0 6px 20px rgba(59, 130, 246, 0.15)';
export const LOGIN_BTN_SHADOW_HOVER_DARK = '0 6px 20px rgba(59, 130, 246, 0.18)';
export const LOGIN_BTN_BORDER_DARK = 'rgba(255, 255, 255, 0.14)';
export const LOGIN_BTN_BORDER_DARK_HOVER = 'rgba(255, 255, 255, 0.22)';
/** Secondary outline button — hover glow (primary) */
export const SECONDARY_BUTTON_PRIMARY_GLOW = '0 0 28px rgba(59, 130, 246, 0.38), 0 6px 22px rgba(59, 130, 246, 0.22)';
export const ROW_HOVER_DARK = 'rgba(255,255,255,0.06)';
export const ROW_HOVER_LIGHT = 'rgba(15,23,42,0.05)';
export const ROW_STRIPE_DARK = 'rgba(255,255,255,0.02)';
export const ROW_STRIPE_LIGHT = 'rgba(0,0,0,0.015)';

// ═══════════════════════════════════════════════════════════════════════════════
// CHROME — toolbars, table (dark UI: white alpha; light UI: separate border token)
// ═══════════════════════════════════════════════════════════════════════════════

export const TOOLBAR_BG_DARK = SURFACE_DARK;
export const TOOLBAR_BG_LIGHT = '#f5f3ff';

/** Subtle grid chrome on dark ambient UI (borders, separators) */
export const TABLE_BORDER_AMBIENT = 'rgba(255,255,255,0.05)';

/** Table / panel on dark surfaces — rgba whites (replaces slate hex stack) */
export const TABLE_HEADER_BG = 'rgba(255,255,255,0.05)';
export const TABLE_PROGRESS_BG = 'rgba(255,255,255,0.04)';
export const TABLE_BORDER_DARK = 'rgba(255,255,255,0.08)';
export const TABLE_BORDER_LIGHT = 'rgba(15, 23, 42, 0.12)';
/** Default border for dark code panels & diff chrome */
export const TABLE_BORDER = TABLE_BORDER_DARK;
export const TABLE_MUTED = 'rgba(255,255,255,0.45)';
export const TABLE_LABEL = 'rgba(255,255,255,0.58)';
export const TABLE_HEADER_TEXT = 'rgba(255,255,255,0.82)';
export const TABLE_TITLE_TEXT = 'rgba(255,255,255,0.92)';

// ═══════════════════════════════════════════════════════════════════════════════
// STATUS
// ═══════════════════════════════════════════════════════════════════════════════

export const SUCCESS = '#2F9E44';
export const SUCCESS_BG = '#E6F4EA';
export const WARNING = '#F59F00';
export const WARNING_BG = '#FFF4E6';
export const DANGER = '#E03131';
export const DANGER_BG = '#FFE3E3';
export const DIFFICULTY_EASY_COLOR = SUCCESS;
export const DIFFICULTY_EASY_BG = SUCCESS_BG;
export const DIFFICULTY_MEDIUM_COLOR = WARNING;
export const DIFFICULTY_MEDIUM_BG = WARNING_BG;
export const DIFFICULTY_HARD_COLOR = DANGER;
export const DIFFICULTY_HARD_BG = DANGER_BG;

// ═══════════════════════════════════════════════════════════════════════════════
// LEADERBOARD / ROW TINTS (primary blue aligned to #3B82F6)
// ═══════════════════════════════════════════════════════════════════════════════

export const GOLD = '#FFD700';
export const GOLD_TEXT = '#7A5800';
export const SILVER = '#E0E0E0';
export const SILVER_TEXT = '#555555';
export const BRONZE = '#CD7F32';
export const GOLD_ROW_DARK = 'rgba(255,215,0,0.06)';
export const GOLD_ROW_LIGHT = 'rgba(255,215,0,0.08)';
export const PRIMARY_ROW_HOVER_DARK = 'rgba(59,130,246,0.12)';
export const PRIMARY_ROW_HOVER_LIGHT = 'rgba(59,130,246,0.06)';
export const WARNING_ROW_DARK = 'rgba(245,159,0,0.07)';
export const WARNING_ROW_LIGHT = 'rgba(245,159,0,0.08)';
export const WARNING_ROW_HOVER_DARK = 'rgba(245,159,0,0.14)';
export const WARNING_ROW_HOVER_LIGHT = 'rgba(245,159,0,0.15)';
export const TH_BG_DARK = 'rgba(255,255,255,0.06)';
export const TH_BG_LIGHT = 'rgba(0,0,0,0.035)';

// ═══════════════════════════════════════════════════════════════════════════════
// PLAGIARISM ACCENTS
// ═══════════════════════════════════════════════════════════════════════════════

export const PLAGIARISM_HIGH_HEX = '#ef4444';
export const PLAGIARISM_MEDIUM_HEX = '#f97316';
export const PLAGIARISM_LOW_HEX = '#22c55e';

// ═══════════════════════════════════════════════════════════════════════════════
// CODE VIEWER / DIFF (aligned to BASE_DARK / SURFACE — no legacy warm gray)
// ═══════════════════════════════════════════════════════════════════════════════

export const CODE_BG_DARK = BASE_DARK;
export const CODE_GUTTER_BG = '#020617';
export const CODE_GUTTER_BG_DARK = '#010409';
export const CODE_FOLD_BG = SURFACE_DARK;
export const CODE_GUTTER_COLOR = 'rgba(255,255,255,0.45)';
export const CODE_BLOCK_SHADOW = 'inset 0 2px 8px rgba(0,0,0,0.2)';
export const DIFF_REMOVED_TEXT = '#f87171';
export const DIFF_ADDED_TEXT = '#4ade80';
export const DIFF_REMOVED_TEXT_LIGHT = '#fca5a5';
export const DIFF_ADDED_TEXT_LIGHT = '#86efac';
export const DIFF_NEUTRAL_TEXT = '#64748b';
export const DIFF_NEUTRAL_TEXT_DARK = 'rgba(255,255,255,0.72)';
export const DIFF_REMOVED_BG = 'rgba(239,68,68,0.15)';
export const DIFF_ADDED_BG = 'rgba(34,197,94,0.13)';
export const DIFF_ADDED_GUTTER_BG = 'rgba(34,197,94,0.22)';
export const DIFF_REMOVED_GUTTER_BG = 'rgba(239,68,68,0.22)';
export const DIFF_WORD_ADDED_BG = 'rgba(34,197,94,0.35)';
export const DIFF_WORD_REMOVED_BG = 'rgba(239,68,68,0.35)';

// ═══════════════════════════════════════════════════════════════════════════════
// PANEL / MODAL CHROME
// ═══════════════════════════════════════════════════════════════════════════════

export const PANEL_SHADOW = '-8px 0 32px rgba(0,0,0,0.18), -2px 0 8px rgba(0,0,0,0.10)';
export const PANEL_BACKDROP_BG = 'rgba(0,0,0,0.25)';
export const PANEL_CLOSE_BTN_BG = SURFACE_DARK;
export const PANEL_CLOSE_BTN_HOVER = 'rgba(255,255,255,0.12)';
export const INDIGO_LIGHTER = '#ddd6fe';

// ═══════════════════════════════════════════════════════════════════════════════
// HELPERS & SHARED SX
// ═══════════════════════════════════════════════════════════════════════════════

export const difficultyColors = difficulty => {
  if (difficulty === 'Easy') return {
    color: DIFFICULTY_EASY_COLOR,
    bg: DIFFICULTY_EASY_BG
  };
  if (difficulty === 'Medium') return {
    color: DIFFICULTY_MEDIUM_COLOR,
    bg: DIFFICULTY_MEDIUM_BG
  };
  if (difficulty === 'Hard') return {
    color: DIFFICULTY_HARD_COLOR,
    bg: DIFFICULTY_HARD_BG
  };
  return {
    color: 'text.secondary',
    bg: 'action.hover'
  };
};
export const difficultyMuiColor = difficulty => {
  if (difficulty === 'Easy') return 'success';
  if (difficulty === 'Medium') return 'warning';
  if (difficulty === 'Hard') return 'error';
  return 'default';
};

/** Page banners — dark: ambient shows through; light: soft gradient */
export const surfaceGradientSx = {
  background: theme => theme.palette.mode === 'dark' ? 'transparent' : GRADIENT_SURFACE_LIGHT,
  border: theme => theme.palette.mode === 'dark' ? '1px solid rgba(255, 255, 255, 0.14)' : undefined
};
export const gridHeaderBgSx = {
  bgcolor: theme => theme.palette.mode === 'dark' ? TH_BG_DARK : HEADER_BG_LIGHT
};
export const gridRowHoverSx = {
  transition: 'background 0.15s',
  '&:hover': {
    bgcolor: theme => theme.palette.mode === 'dark' ? ROW_HOVER_DARK : ROW_HOVER_LIGHT
  }
};
export const gridRowStripeSx = {
  '& .MuiDataGrid-row:nth-of-type(even)': {
    bgcolor: theme => theme.palette.mode === 'dark' ? ROW_STRIPE_DARK : ROW_STRIPE_LIGHT
  }
};

/** Primary / secondary cell text on dark ambient tables */
export const TABLE_TEXT_PRIMARY_DARK = '#E5E7EB';
export const TABLE_TEXT_SECONDARY_DARK = 'rgba(255, 255, 255, 0.6)';

/**
 * MUI DataGrid — dark ambient: transparent body, TH_BG_DARK headers, ROW_HOVER_DARK,
 * TABLE_BORDER_AMBIENT dividers, white primary cell text.
 */
export const dataGridAmbientBaseSx = {
  border: 'none',
  bgcolor: theme => theme.palette.mode === 'dark' ? 'transparent' : undefined,
  '& .MuiDataGrid-root': {
    borderColor: theme => theme.palette.mode === 'dark' ? TABLE_BORDER_AMBIENT : undefined
  },
  '& .MuiDataGrid-main': {
    bgcolor: theme => theme.palette.mode === 'dark' ? 'transparent' : undefined
  },
  '& .MuiDataGrid-virtualScroller': {
    bgcolor: theme => theme.palette.mode === 'dark' ? 'transparent' : undefined
  },
  '& .MuiDataGrid-virtualScrollerContent': {
    bgcolor: theme => theme.palette.mode === 'dark' ? 'transparent' : undefined
  },
  '& .MuiDataGrid-cell': {
    borderColor: theme => theme.palette.mode === 'dark' ? TABLE_BORDER_AMBIENT : 'divider',
    color: theme => theme.palette.mode === 'dark' ? TABLE_TEXT_PRIMARY_DARK : undefined
  },
  '& .MuiDataGrid-columnHeaders': {
    bgcolor: theme => theme.palette.mode === 'dark' ? TH_BG_DARK : HEADER_BG_LIGHT,
    borderBottom: theme => theme.palette.mode === 'dark' ? `1px solid ${TABLE_BORDER_AMBIENT}` : '1px solid',
    borderColor: theme => theme.palette.mode === 'dark' ? TABLE_BORDER_AMBIENT : 'divider'
  },
  '& .MuiDataGrid-columnHeaderTitle': {
    color: theme => theme.palette.mode === 'dark' ? TABLE_TEXT_PRIMARY_DARK : SLATE_900
  },
  '& .MuiDataGrid-columnSeparator': {
    color: theme => theme.palette.mode === 'dark' ? TABLE_BORDER_AMBIENT : undefined
  },
  '& .MuiDataGrid-footerContainer': {
    borderTop: theme => theme.palette.mode === 'dark' ? `1px solid ${TABLE_BORDER_AMBIENT}` : '1px solid',
    borderColor: theme => theme.palette.mode === 'dark' ? TABLE_BORDER_AMBIENT : 'divider',
    bgcolor: theme => theme.palette.mode === 'dark' ? 'transparent' : undefined
  },
  '& .MuiDataGrid-toolbarContainer': {
    bgcolor: theme => theme.palette.mode === 'dark' ? 'transparent' : undefined,
    borderBottom: theme => theme.palette.mode === 'dark' ? `1px solid ${TABLE_BORDER_AMBIENT}` : undefined
  },
  '& .MuiDataGrid-overlay': {
    bgcolor: theme => theme.palette.mode === 'dark' ? 'transparent' : undefined
  },
  '& .MuiDataGrid-row': {
    transition: 'background 0.15s',
    '&:hover': {
      bgcolor: theme => theme.palette.mode === 'dark' ? ROW_HOVER_DARK : ROW_HOVER_LIGHT
    }
  },
  '& .MuiDataGrid-row:nth-of-type(even)': {
    bgcolor: theme => theme.palette.mode === 'dark' ? ROW_STRIPE_DARK : ROW_STRIPE_LIGHT
  }
};

/** Contained primary — solid PRIMARY, hover lift via SHADOW_PRIMARY_HOVER */
export const gradientButtonSx = {
  textTransform: 'none',
  fontWeight: fw.bold,
  borderRadius: 3,
  color: c.c_ffffff,
  backgroundColor: PRIMARY,
  boxShadow: SHADOW_PRIMARY,
  '&:hover': {
    backgroundColor: PRIMARY,
    boxShadow: SHADOW_PRIMARY_HOVER
  }
};

/** Outlined / secondary — transparent, LOGIN border; hover primary glow */
export const outlinedPrimaryButtonSx = {
  textTransform: 'none',
  fontWeight: fw.semiBold,
  borderRadius: 3,
  bgcolor: 'transparent',
  border: '1px solid',
  borderColor: theme => theme.palette.mode === 'dark' ? LOGIN_BTN_BORDER_DARK : LOGIN_BTN_BORDER,
  color: PRIMARY,
  '&:hover': {
    bgcolor: 'transparent',
    borderColor: theme => theme.palette.mode === 'dark' ? LOGIN_BTN_BORDER_DARK_HOVER : LOGIN_BTN_BORDER_HOVER,
    boxShadow: theme => theme.palette.mode === 'dark' ? SECONDARY_BUTTON_PRIMARY_GLOW : LOGIN_BTN_SHADOW_HOVER
  }
};
export const primaryChipSx = {
  fontWeight: fw.bold,
  bgcolor: PRIMARY_LIGHT,
  color: PRIMARY,
  border: 'none'
};
export const gradientTextSx = {
  background: GRADIENT_TEXT,
  WebkitBackgroundClip: 'text',
  WebkitTextFillColor: 'transparent'
};

// ═══════════════════════════════════════════════════════════════════════════════
// LOGIN PAGE — card, veil, Google button (uses SLATE_900 + shell tokens above)
// ═══════════════════════════════════════════════════════════════════════════════

export const LOGIN_MAIN_VEIL_DARK = 'rgba(0, 0, 0, 0.08)';
export const LOGIN_MAIN_VEIL_LIGHT = 'rgba(255, 255, 255, 0.02)';
export const LOGIN_LEFT_OVERLAY = ['linear-gradient(180deg, rgba(255,255,255,0.022) 0%, rgba(2,6,23,0.28) 100%)', 'linear-gradient(90deg, rgba(0,0,0,0.1) 0%, transparent 58%)'].join(', ');
export const LOGIN_RIGHT_BG_DARK = [`linear-gradient(180deg, ${PORTAL_SHELL_TO} 0%, ${PORTAL_SHELL_FROM} 100%)`, 'radial-gradient(circle at 72% 48%, rgba(99, 102, 241, 0.06), transparent 55%)', 'radial-gradient(ellipse 90% 70% at 78% 45%, rgba(255, 255, 255, 0.04), transparent 58%)'].join(', ');
export const LOGIN_RIGHT_BG_LIGHT = [BASE_LIGHT_GRADIENT, 'radial-gradient(circle at 70% 45%, rgba(99, 102, 241, 0.05), transparent 52%)', 'radial-gradient(ellipse 100% 80% at 82% 50%, rgba(255, 255, 255, 0.14), transparent 60%)'].join(', ');
export const LOGIN_COLUMN_EDGE_DARK = `linear-gradient(to right, rgba(${SLATE_900_RGB}, 0.4), transparent)`;
export const LOGIN_COLUMN_EDGE_LIGHT = `linear-gradient(to right, rgba(${SLATE_900_RGB}, 0.05), transparent)`;
export const LOGIN_CARD_BORDER = 'rgba(255, 255, 255, 0.14)';
export const LOGIN_CARD_BG_LIGHT = 'rgba(255, 255, 255, 0.65)';
export const LOGIN_CARD_BG_DARK = `rgba(${SLATE_900_RGB}, 0.74)`;
export const LOGIN_CARD_DIVIDER_LIGHT = `rgba(${SLATE_900_RGB}, 0.08)`;
export const LOGIN_CARD_SHADOW_AMBIENT = '0 0 44px rgba(99, 102, 241, 0.07)';
export const LOGIN_CARD_SHADOW_BASE_DARK = `0 8px 25px rgba(0, 0, 0, 0.36), ${LOGIN_CARD_SHADOW_AMBIENT}, inset 0 1px 0 rgba(255, 255, 255, 0.05)`;
export const LOGIN_CARD_SHADOW_BASE_LIGHT = `0 8px 25px rgba(0, 0, 0, 0.2), ${LOGIN_CARD_SHADOW_AMBIENT}`;
export const LOGIN_CARD_SHADOW_HOVER_DARK = '0 10px 30px rgba(0, 0, 0, 0.42), 0 0 52px rgba(99, 102, 241, 0.09), inset 0 1px 0 rgba(255, 255, 255, 0.06)';
export const LOGIN_CARD_SHADOW_HOVER_LIGHT = '0 10px 30px rgba(0, 0, 0, 0.18), 0 0 52px rgba(99, 102, 241, 0.08)';

/** Recharts / inline tooltips — matches login glass card */
export const dashboardTooltipSurfaceSx = {
  bgcolor: theme => theme.palette.mode === 'dark' ? LOGIN_CARD_BG_DARK : 'background.paper',
  backdropFilter: theme => theme.palette.mode === 'dark' ? 'blur(10px)' : 'none',
  WebkitBackdropFilter: theme => theme.palette.mode === 'dark' ? 'blur(10px)' : 'none',
  border: '1px solid',
  borderColor: theme => theme.palette.mode === 'dark' ? LOGIN_CARD_BORDER : 'divider',
  boxShadow: theme => theme.palette.mode === 'dark' ? LOGIN_CARD_SHADOW_BASE_DARK : 3
};

/** Muted inset panels (replaces grey.50 on dark) */
export const panelMutedSurfaceSx = {
  bgcolor: theme => theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.04)' : 'grey.50'
};
export const WHITE = '#ffffff';
export const LOGIN_BTN_BG_DARK = 'rgba(255, 255, 255, 0.98)';
